import type {
    DisplayOptionType,
    FlattenedCarType,
    LeasingConfigurationType,
    SelectGradeType,
    SetCarDataType,
} from "./compareTypes";
import type {
    ModelGradeBodyTypeIdsType,
    CompareMotorizationType,
    AvailableColoursType,
    SpecItemType,
} from "../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import { PDF_CAR_SEPARATOR } from "../../../shared-logic/features/compare/utils/constants";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import { WLTPLabelsMap } from "../../../common-deprecated/utils/t1labels";
import type { FetchFnReturnType } from "../../../common-deprecated/utils/backendFetch";
import { getCompareLocalStorage } from "./localStorage";
import { NcCompareContext } from "../../../common-deprecated/types/NewCarCompareTypes";
import { CERTIFICATION_WLTP } from "../../../common-deprecated/constants";

export const getModelGradeBodyTypeIdsAndEngineIdsFromUrl = (
    queryInput: string | null,
): {
    modelGradeBodyTypeIds: ModelGradeBodyTypeIdsType[];
    motorizationIds: CompareMotorizationType[];
    colourIds: string[];
} => {
    if (!queryInput) return { modelGradeBodyTypeIds: [], motorizationIds: [], colourIds: [] };
    // queryCarIds will be formatted as followed =>
    // modelId : submodelId: gradeId: bodytypeId : engineId : wheeldriveId : transmissionId : colourCode | modelId : submodelId: gradeId: bodytypeId : engineId : wheeldriveId : transmissionId : colourCode
    const cars = queryInput.split(PDF_CAR_SEPARATOR);

    const modelGradeBodyTypeIds: ModelGradeBodyTypeIdsType[] = [];
    const motorizationIds: CompareMotorizationType[] = [];
    const colourIds: string[] = [];

    try {
        cars.forEach((car) => {
            const ids = car.split(":");

            // id[1] is the submodelId, this is not a required ID
            if (!(ids[0] && ids[2] && ids[3] && ids[4] && ids[5] && ids[6])) {
                throw new Error("not all id's are found in the Query parameter");
            }

            modelGradeBodyTypeIds.push({
                model: ids[0],
                submodelId: ids[1],
                gradeId: ids[2],
                bodyTypeId: ids[3],
                configurationId: ids[8],
            });
            motorizationIds.push({
                engineId: ids[4],
                engineName: "",
                wheeldriveId: ids[5],
                wheeldriveName: "",
                transmissionId: ids[6],
                transmissionName: "",
            });
            colourIds.push(ids[7]);
        });

        return { modelGradeBodyTypeIds, motorizationIds, colourIds };
    } catch (e) {
        return { modelGradeBodyTypeIds: [], motorizationIds: [], colourIds: [] };
    }
};

export const getModelGradeBodyTypeIdsStringForQueryParams = (
    modelGradeBodyTypeIds: ModelGradeBodyTypeIdsType[],
    engineIds: CompareMotorizationType[],
    colourIds: string[],
): string => {
    return modelGradeBodyTypeIds
        .map((modelGradeBodyTypeId, index) => {
            return [
                modelGradeBodyTypeId.model,
                modelGradeBodyTypeId.submodelId || "",
                modelGradeBodyTypeId.gradeId,
                modelGradeBodyTypeId.bodyTypeId,
                engineIds[index].engineId,
                engineIds[index].wheeldriveId,
                engineIds[index].transmissionId,
                colourIds[index],
                modelGradeBodyTypeId.configurationId || "",
            ].join(":");
        })
        .join(PDF_CAR_SEPARATOR);
};

export const getDisplayOptionsParams = (displayOptions: { [key in DisplayOptionType["type"]]: boolean }): string => {
    return Object.entries(displayOptions)
        .map(([type, value]) => `onlyShowDifferences${type}=${value.toString()}`)
        .join("&");
};

export const getColourCode = (query: string, availableColours: AvailableColoursType[]): string => {
    // When a car is added from the grade explorer we only have the hex code,
    // GFI sends the GUID, so we check for all possibilities.
    const correctColour = availableColours.find((availableColour) => {
        return (
            availableColour.rgb === query ||
            availableColour.id === query ||
            availableColour.code === query ||
            `#${availableColour.rgb}` === query
        );
    });

    return correctColour ? correctColour.code : availableColours[0].code;
};

export const shouldShowSpecList = (onlyShowDifferences: boolean, specs: SpecItemType[]): boolean => {
    let show = false;
    if (onlyShowDifferences) {
        specs
            .map((spec) => spec.specs)
            .forEach((subSpecs) => {
                subSpecs.forEach((specLine) => {
                    if (!specLine.allValuesAreTheSame) {
                        show = true;
                    }
                });
            });
    } else {
        show = true;
    }

    return show;
};

// GFI and we sometimes send either modelId or modelCode postal events / redux actions
// but we need to request the cardata from the server with a modelCode as it also contains generational information
export const mapPossibleModelIdToCode = (
    ids: ModelGradeBodyTypeIdsType,
    availableGrades: SelectGradeType[],
    settings: CommonSettingsType,
): string => {
    let modelCode: string;

    // first try to get a modelmap value by checking if the typeless model is a modelCode, return code if it is
    const foundModelMapValueByCode = Object.values(settings.modelMap).find(
        (value) => value.internalCode.toLowerCase() === ids.model.toLowerCase(),
    );
    if (foundModelMapValueByCode) {
        modelCode = foundModelMapValueByCode.internalCode;
    } else {
        // we suppose we were supplied a modelId instead, we need to use this in conjunction with
        // the gradeId and all available grades to figure out if this modelId should become a current or next generation modelCode
        const modelContainingGradeId = availableGrades.find((model) =>
            model.subModels.find((submodel) => submodel.grades.find((grade) => grade.gradeId === ids.gradeId)),
        );
        modelCode = modelContainingGradeId?.modelCode || "";
    }
    return modelCode;
};

export const getWLTPLabelKey = (certification: number, isElectric?: boolean): string => {
    if (certification === CERTIFICATION_WLTP) return isElectric ? "T1.disclaimerWltpElectric" : "T1.disclaimerWltp";
    return WLTPLabelsMap[certification];
};

export const getComparePDFLink = (
    commonSettings: CommonSettingsType,
    compareContext: NcCompareContext = NcCompareContext.Original,
    available: SelectGradeType[] = [],
): string => {
    const baseUrl = `${commonSettings.resourcePath}/${commonSettings.country}/${commonSettings.language}/compare-v2`;

    const { modelGradeBodyTypeIds, motorizationIds, colourIds, displayOptions } = getCompareLocalStorage(
        commonSettings,
        compareContext,
    );
    const displayOptionsParams = getDisplayOptionsParams(displayOptions);

    // since we do not know if local storage contains modelId or modelCode we need to map this
    const allCarIds = modelGradeBodyTypeIds.map((ids) => ({
        model: mapPossibleModelIdToCode(ids, available, commonSettings),
        submodelId: ids.submodelId,
        gradeId: ids.gradeId,
        bodyTypeId: ids.bodyTypeId,
        configurationId: ids.configurationId,
    }));

    const queryParams = getModelGradeBodyTypeIdsStringForQueryParams(allCarIds, motorizationIds, colourIds);

    return `${baseUrl}/pdf?compareData=${queryParams}&${displayOptionsParams}&ncCompareContext=${compareContext}`;
};

export const isModelCodeNewGeneration = (settings: CommonSettingsType, modelCode: string): boolean =>
    Object.values(settings.modelMap).find((value) => value.internalCode.toLowerCase() === modelCode.toLowerCase())
        ?.isNewGeneration || false;

export const getCompareCarIds = (
    compareData: string,
): {
    carIds: { modelCode: string; submodelId: string; gradeId: string; bodyTypeId: string }[];
    motorizationIds: CompareMotorizationType[];
    colourIds: string[];
} => {
    const fetchData = getModelGradeBodyTypeIdsAndEngineIdsFromUrl(compareData);
    const { modelGradeBodyTypeIds, motorizationIds, colourIds } = fetchData;

    // pdf endpoint should only be called with modelCodes instead of modelId, we can do a simple map here because of this
    const carIds = modelGradeBodyTypeIds.map((ids) => ({
        modelCode: ids.model,
        submodelId: ids.submodelId,
        gradeId: ids.gradeId,
        bodyTypeId: ids.bodyTypeId,
        configurationId: ids.configurationId,
    }));

    return {
        carIds,
        motorizationIds,
        colourIds,
    };
};

export const getCompareMotorization = (
    result: FetchFnReturnType<SetCarDataType>[],
    motorizationIds: CompareMotorizationType[],
    colourIds: string[],
): SetCarDataType[] => {
    return result.map((resultWithoutCorrectEngine, index) => {
        const correctEngine = resultWithoutCorrectEngine.fetchResult.enginesWithSpecs.find(
            (engine) =>
                motorizationIds[index] &&
                engine.engineId === motorizationIds[index].engineId &&
                engine.transmissionId === motorizationIds[index].transmissionId &&
                engine.wheeldriveId === motorizationIds[index].wheeldriveId,
        );

        if (correctEngine) {
            const { ...selectedEngine } = correctEngine;
            resultWithoutCorrectEngine.fetchResult.selectedEngine = selectedEngine;
        }

        // Set the selected Colour.
        resultWithoutCorrectEngine.fetchResult.carData.colourCode = colourIds[index];

        return resultWithoutCorrectEngine.fetchResult;
    });
};

export const getConfiguration = (
    available: SelectGradeType[],
    modelGradeBodyTypeIds: ModelGradeBodyTypeIdsType,
): LeasingConfigurationType | null => {
    const { configurationId, gradeId, bodyTypeId, submodelId, model } = modelGradeBodyTypeIds;
    return (
        available
            .find((value) => value.modelId === model)
            ?.subModels.find((subModel) => subModel.subModelId === submodelId)
            ?.grades.find((grade) => grade.gradeId === gradeId)
            ?.bodyTypes.find((bodyType) => bodyType.bodyTypeId === bodyTypeId)
            ?.configurations?.find((configuration) => configuration.id === configurationId) || null
    );
};

export const getGradeConfigurations = (
    selectGrade: SelectGradeType,
    selectedConfigurations: (string | undefined)[],
    availableCongifurationIds: string[],
): FlattenedCarType[] => {
    return selectGrade.subModels
        .reduce((modelAccumulator: FlattenedCarType[], subModel) => {
            return subModel.grades.reduce((subModelAccumulator: FlattenedCarType[], grade) => {
                return grade.bodyTypes.reduce((gradeAccumulator: FlattenedCarType[], bodyType) => {
                    return bodyType.configurations!.reduce((configurationAccumulator: FlattenedCarType[], config) => {
                        // Only add the first pack-less configuration to avoid duplicate names
                        // We're unable show any meaningful difference between configs to the user (eg. wheeldriveName)
                        const configIdentifier =
                            grade.gradeId +
                            bodyType.bodyTypeId +
                            subModel.subModelId +
                            config.packs?.map((pack) => pack.id).join("");
                        if (
                            configurationAccumulator.some((option) => {
                                return (
                                    configIdentifier ===
                                    option.gradeId +
                                        option.bodyTypeId +
                                        option.subModelId +
                                        option.packs?.map((pack) => pack.id).join("")
                                );
                            })
                        ) {
                            return configurationAccumulator;
                        }
                        return configurationAccumulator.concat({
                            gradeId: grade.gradeId,
                            bodyTypeId: bodyType.bodyTypeId,
                            subModelId: subModel.subModelId,
                            subModelName: subModel.subModelName,
                            bodyTypeName: bodyType.bodyTypeName,
                            gradeName: grade.gradeName,
                            configurationId: config.id,
                            packs: config.packs,
                        });
                    }, gradeAccumulator);
                }, subModelAccumulator);
            }, modelAccumulator);
        }, [])
        .filter(
            (config) =>
                config.configurationId &&
                !selectedConfigurations.includes(config.configurationId) &&
                availableCongifurationIds.includes(config.configurationId),
        );
};
